import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Text from 'UIKit/Text'
import {
  BonusStatusEnums,
  BonusStep,
  ICommonBonus,
  IWageringCondition,
  IWageringConditionBigAmount,
  WageringBonusStatusEnums,
} from 'config/types/bonus'
import { DepositFreespinBonus, FreespinBonus, WageringConditionBonus } from 'config/types/bonus/userBonus'
import React from 'react'
import { Trans } from 'react-i18next'
import BonusDetailStepCard from '../BonusDetailStepCard'
import InstructionDetails from '../InstructionDetails'
import ActivationInfoSection from './ActivationInfoSection'
import BonusNameSectionDetails from './BonusNameSectionDetails'
import DepositFreespinBonusInfoSection from './DepositFreespinBonusInfoSection'
import WageringInfoSectionDetails from './WageringInfoSectionDetails'

interface WageringBonusConditionDetailsProps {
  bonus: IWageringCondition & ICommonBonus & IWageringConditionBigAmount
}

const ClaimBonusStep: React.FC<{ step: number; isActive: boolean; isProcessing: boolean; isEndStep?: boolean }> = ({
  step,
  isActive,
  isProcessing,
  isEndStep,
}) => {
  return (
    <BonusDetailStepCard
      disabledCollapse
      isActive={isActive}
      isProcessing={isProcessing}
      cardTitle={
        <Flex flexDirection="column">
          <Text
            textTransform="uppercase"
            fontSize="10px"
            fontWeight="600"
            ml="8px"
            lineHeight="normal"
            color="textAlt1"
          >
            <Trans>Step {{ step }}</Trans>
          </Text>
          <Text fontSize="14px" fontWeight="600" mb="4px" ml="8px" lineHeight="22px">
            <Trans>Claim your bonus</Trans>
          </Text>
        </Flex>
      }
    >
      {!isEndStep && <Box borderRadius="8px" ml="8px" mt="12px" />}
    </BonusDetailStepCard>
  )
}

const WageringBonusConditionDetails: React.FC<WageringBonusConditionDetailsProps> = ({ bonus }) => {
  return (
    <Box overflow="hidden">
      <BonusNameSectionDetails bonus={bonus} />

      {bonus.stepInfomation ? (
        bonus.stepInfomation
          .sort((a, b) => a.step - b.step)
          .map(({ step, stepContent }) => {
            switch (stepContent) {
              case BonusStep.Active:
                return (
                  <ActivationInfoSection
                    mt={step > 1 && '20px'}
                    key={step}
                    bonus={bonus}
                    step={step}
                    isActive={bonus instanceof WageringConditionBonus && bonus.currentStep > step}
                    isProcessing={bonus instanceof WageringConditionBonus && bonus.currentStep === step}
                  />
                )
              case BonusStep.Claim:
                return (
                  <Box key={step} mt={step > 1 && '20px'}>
                    <ClaimBonusStep
                      isEndStep={step === bonus.stepInfomation.length}
                      isActive={bonus instanceof WageringConditionBonus && bonus.currentStep > step}
                      isProcessing={bonus instanceof WageringConditionBonus && bonus.currentStep === step}
                      step={step}
                    />
                  </Box>
                )
              case BonusStep.MakeSpin:
                return (
                  <DepositFreespinBonusInfoSection
                    mt={step > 1 && '20px'}
                    bonus={bonus as DepositFreespinBonus}
                    step={step}
                    isActive={bonus instanceof WageringConditionBonus && bonus.currentStep > step}
                    isProcessing={bonus instanceof WageringConditionBonus && bonus.currentStep === step}
                  />
                )
              case BonusStep.Wagering:
                return (
                  <WageringInfoSectionDetails
                    mt={step > 1 && '20px'}
                    bonus={bonus}
                    step={step}
                    isActive={bonus instanceof WageringConditionBonus && bonus.currentStep > step}
                    isProcessing={bonus instanceof WageringConditionBonus && bonus.currentStep === step}
                  />
                )

              default:
                return null
            }
          })
      ) : (
        <>
          <ActivationInfoSection
            bonus={bonus}
            step={1}
            isActive={bonus.status === BonusStatusEnums.Active || bonus.status === BonusStatusEnums.Used}
            isProcessing={bonus.status === BonusStatusEnums.Available}
          />

          {bonus instanceof DepositFreespinBonus && (
            <DepositFreespinBonusInfoSection
              mt={20}
              isActive={
                bonus.wageringBonusStatus === WageringBonusStatusEnums.InProgressWagering ||
                bonus.wageringBonusStatus === WageringBonusStatusEnums.Claimed ||
                bonus.wageringBonusStatus === WageringBonusStatusEnums.CreditRewardWaitingRedeem
              }
              isProcessing={bonus.wageringBonusStatus === WageringBonusStatusEnums.WaitingSpin}
              bonus={bonus}
              step={2}
            />
          )}

          {bonus.isCreaditReward && (bonus instanceof FreespinBonus || bonus instanceof DepositFreespinBonus) && (
            <Box mt="20px">
              <ClaimBonusStep
                isActive={
                  bonus.wageringBonusStatus === WageringBonusStatusEnums.InProgressWagering ||
                  bonus.currentWagerBigAmount.gt(0)
                }
                isProcessing={bonus.wageringBonusStatus === WageringBonusStatusEnums.CreditRewardWaitingRedeem}
                step={3}
              />
            </Box>
          )}

          <WageringInfoSectionDetails
            mt={20}
            bonus={bonus}
            step={bonus instanceof DepositFreespinBonus ? (bonus.isCreaditReward ? 4 : 3) : 2}
            isActive={
              bonus instanceof WageringConditionBonus
                ? (bonus.wageringBonusStatus === WageringBonusStatusEnums.InProgressWagering &&
                    bonus.progress >= 100) ||
                  (bonus.status === BonusStatusEnums.Used && bonus.progress >= 100)
                : false
            }
            isProcessing={
              bonus instanceof WageringConditionBonus
                ? bonus.wageringBonusStatus === WageringBonusStatusEnums.InProgressWagering &&
                  bonus.progress < 100 &&
                  bonus.isActiveWagering
                : false
            }
          />

          {!bonus.isCreaditReward && !(bonus instanceof WageringConditionBonus && bonus.isActiveMilestone) && (
            <Box mt="20px">
              <ClaimBonusStep
                isEndStep
                isActive={bonus.wageringBonusStatus === WageringBonusStatusEnums.Claimed}
                isProcessing={
                  bonus instanceof WageringConditionBonus
                    ? bonus.wageringBonusStatus === WageringBonusStatusEnums.InProgressWagering && bonus.progress >= 100
                    : false
                }
                step={bonus instanceof DepositFreespinBonus ? 4 : 3}
              />
            </Box>
          )}
        </>
      )}

      <InstructionDetails bonus={bonus} mt="28px" ml="12px" />
    </Box>
  )
}

export default WageringBonusConditionDetails
